import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../auth/authUtils.ts';
import { useConfig } from '../../../contexts/configContext.ts';
import { useOrganisationsContext } from '../../../contexts/useOrganisationsContext.ts';
import { deactivateDeckPlanRequest } from '../../../graphql/vehicles/mutations/deactivateDeckPlan.ts';
import type { DeckPlan } from '../types/deckPlanTypes.ts';

interface SaveResult {
  newVersion: number | null;
  error: string | null;
}

interface UseDeckPlanDeactivateResult {
  deactivate: (form: DeckPlan) => Promise<SaveResult>;
  saving: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * Deactivate a DeckPlan via the `deactivateDeckPlan` GraphQL mutation.
 *
 * @returns `deactivate` (→ `{ newVersion, error }`), plus `saving`, `error`, `clearError`.
 */
export function useDeckPlanDeactivate(): UseDeckPlanDeactivateResult {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const { applicationBaseUrl } = useConfig();
  const { currentOrganisation } = useOrganisationsContext();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deactivate = useCallback(
    async (form: DeckPlan): Promise<SaveResult> => {
      if (!applicationBaseUrl) {
        const message = t('error.noBaseUrl');
        setError(message);
        return { newVersion: null, error: message };
      }
      if (!currentOrganisation?.id) {
        const message = t('deckPlans.deactivate.noOrganisation');
        setError(message);
        return { newVersion: null, error: message };
      }
      setSaving(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const body = await deactivateDeckPlanRequest(applicationBaseUrl, token, {
          netexId: form.id,
          version: Number(form.version),
          deactivateAt: new Date().toISOString(),
        });
        return { newVersion: body.deactivateDeckPlan?.version ?? null, error: null };
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setError(message);
        return { newVersion: null, error: message };
      } finally {
        setSaving(false);
      }
    },
    [applicationBaseUrl, getAccessToken, currentOrganisation?.id, t]
  );

  return { deactivate, saving, error, clearError: () => setError(null) };
}
